<script setup lang="ts">
/**
 * Module: web/src/views/MyEntriesView.vue
 * Author: Salvo Praxis (w/ AI pair programming partner Claude Code by Anthropic)
 * Version: 1.3.0
 *
 * v1.3.0 (decision #210 B): back-entry chips on both surfaces --
 * "Entered for <dispatcher>" on rows you recorded for someone else,
 * "Entered by <recorder>" on rows someone else recorded under your
 * attribution (the /my-entries scope is now dispatcher-OR-entered_by).
 * Date: 2026-07-20
 *
 * v1.2.0 (auto-refresh -- the AssistedEntryView 2.4.0 pattern): 2-min
 * visibility-gated poll, skipped while the entry modal is open; the
 * Loading state now gates on an empty list so refreshes are silent.
 *
 * Driver Workspace landing (doc 04 sect 9.5, locked A2) -- REAL view:
 * read-only "your recent entries" from GET /my-entries. No cap gate; the API
 * scopes rows to the session identity server-side. Never a 404 for a driver.
 * Mobile-first: stacked cards on phones, table on md+.
 *
 * v1.1.0 (AE slice 4, contract entry-log-api v1.0.0 SS4): rows open the
 * shared EntryDetailModal for sessions holding data_entry (dispatchers
 * editing their OWN entries -- the modal's Unlock rides the existing
 * own-or-manager PUT rule). Sessions without data_entry (pure drivers)
 * keep the read-only list exactly as before: the modal's GET would 403
 * them, so no click affordance is shown.
 */
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { apiGet } from '@/composables/useApi';
import { useAuthStore } from '@/stores/auth';
import type { MyEntriesResponse, MyEntry } from '@/types/api';
import AppIcon from '@/components/AppIcon.vue';
import EntryDetailModal from '@/components/entries/EntryDetailModal.vue';

const auth = useAuthStore();
const entries = ref<MyEntry[]>([]);
const loading = ref(true);
const errorMessage = ref('');

// ---- the shared modal (data_entry holders only -- see header) -------------------
const canOpen = auth.hasCap('data_entry');
const modalOpen = ref(false);
const modalEntryId = ref<number | null>(null);

function openEntry(e: MyEntry) {
  if (!canOpen) return;
  modalEntryId.value = e.id;
  modalOpen.value = true;
}

function onSaved() {
  void load(); // refresh after an edit
}

function fmtQuote(q: MyEntry['quote_given']): string {
  if (q === null || q === undefined) return '—';
  if (typeof q === 'boolean') return q ? 'Yes' : 'No';
  if (typeof q === 'number') return q.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  return q;
}

async function load() {
  loading.value = true;
  errorMessage.value = '';
  try {
    const resp = await apiGet<MyEntriesResponse>('/my-entries');
    entries.value = resp?.entries ?? [];
  } catch {
    errorMessage.value = 'Could not load your entries. Pull to refresh or try again.';
  } finally {
    loading.value = false;
  }
}

onMounted(load);

// ---- v1.2.0 auto-refresh (the AssistedEntryView 2.4.0 pattern) -------------
// Politeness guard: no polling while the entry modal is open (an unlocked
// edit lives in the modal; onSaved already refreshes). The template's
// Loading state gates on an EMPTY list so background refreshes never
// replace the table with "Loading…".
const POLL_MS = 120_000;
let pollTimer: ReturnType<typeof setInterval> | undefined;

function pollTick() {
  if (document.hidden || loading.value || modalOpen.value) return;
  void load();
}

function onVisibility() {
  if (!document.hidden) pollTick();
}

onMounted(() => {
  pollTimer = setInterval(pollTick, POLL_MS);
  document.addEventListener('visibilitychange', onVisibility);
});

onBeforeUnmount(() => {
  clearInterval(pollTimer);
  document.removeEventListener('visibilitychange', onVisibility);
});
</script>

<template>
  <div class="px-4 py-5 md:p-6 max-w-5xl">
    <div class="flex items-center justify-between gap-3 mb-4">
      <div>
        <h1 class="text-xl font-bold text-fg">My Entries</h1>
        <p class="text-sm text-fg-muted">Your recent missed-opportunity entries — read-only.</p>
      </div>
      <button class="btn-secondary !h-10 !px-3 text-sm" :disabled="loading" @click="load">Refresh</button>
    </div>

    <div v-if="errorMessage" class="alert-error mb-4" role="alert">{{ errorMessage }}</div>

    <div v-if="loading && entries.length === 0" class="text-sm text-fg-muted py-8 text-center">Loading…</div>

    <div v-else-if="entries.length === 0" class="bg-card border border-border-c rounded-lg p-8 text-center">
      <AppIcon name="list" class="w-8 h-8 text-fg-muted mx-auto mb-2" />
      <p class="text-sm text-fg-muted">No entries yet.</p>
    </div>

    <template v-else>
      <p v-if="canOpen" class="text-xs text-fg-muted mb-3">
        Tap an entry to view or edit it.
      </p>
      <!-- Mobile: stacked cards -->
      <div class="md:hidden space-y-3">
        <div
          v-for="e in entries"
          :key="e.id"
          class="bg-card border border-border-c rounded-lg p-4 shadow-card"
          :class="canOpen ? 'cursor-pointer hover:border-accent/50' : ''"
          @click="openEntry(e)"
        >
          <div class="flex items-center justify-between gap-2 mb-2">
            <span class="font-semibold text-fg">{{ e.call_date }}</span>
            <span class="text-sm text-fg-muted">{{ e.call_time }}</span>
          </div>
          <dl class="grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm">
            <dt class="text-fg-muted">Service</dt><dd class="text-fg text-right">{{ e.service ?? '—' }}</dd>
            <dt class="text-fg-muted">Duty</dt><dd class="text-fg text-right">{{ e.duty ?? '—' }}</dd>
            <dt class="text-fg-muted">Quote given</dt><dd class="text-fg text-right">{{ fmtQuote(e.quote_given) }}</dd>
          </dl>
          <div v-if="e.reasons?.length" class="mt-2.5 flex flex-wrap gap-1.5">
            <span
              v-for="r in e.reasons"
              :key="r"
              class="text-xs px-2 py-0.5 rounded-full bg-input text-fg-muted border border-border-c"
            >{{ r }}</span>
          </div>
          <div v-if="e.entered_for || e.entered_by_name" class="mt-2">
            <span class="text-xs px-2 py-0.5 rounded-full bg-accent/10 text-accent border border-accent/30">
              {{ e.entered_for ? `Entered for ${e.entered_for}` : `Entered by ${e.entered_by_name}` }}
            </span>
          </div>
        </div>
      </div>

      <!-- Desktop: table -->
      <div class="hidden md:block bg-card border border-border-c rounded-lg overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-xs uppercase tracking-wide text-fg-muted border-b border-border-c">
                <th class="px-4 py-3 font-medium">Date</th>
                <th class="px-4 py-3 font-medium">Time</th>
                <th class="px-4 py-3 font-medium">Service</th>
                <th class="px-4 py-3 font-medium">Duty</th>
                <th class="px-4 py-3 font-medium">Quote given</th>
                <th class="px-4 py-3 font-medium">Reasons</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="e in entries"
                :key="e.id"
                class="border-b border-border-c/60 last:border-0 hover:bg-input/40"
                :class="canOpen ? 'cursor-pointer' : ''"
                @click="openEntry(e)"
              >
                <td class="px-4 py-2.5 text-fg whitespace-nowrap">
                  {{ e.call_date }}
                  <span
                    v-if="e.entered_for || e.entered_by_name"
                    class="block mt-0.5 text-xs text-accent whitespace-nowrap"
                  >{{ e.entered_for ? `entered for ${e.entered_for}` : `entered by ${e.entered_by_name}` }}</span>
                </td>
                <td class="px-4 py-2.5 text-fg-muted whitespace-nowrap">{{ e.call_time }}</td>
                <td class="px-4 py-2.5 text-fg">{{ e.service ?? '—' }}</td>
                <td class="px-4 py-2.5 text-fg-muted">{{ e.duty ?? '—' }}</td>
                <td class="px-4 py-2.5 text-fg">{{ fmtQuote(e.quote_given) }}</td>
                <td class="px-4 py-2.5">
                  <div class="flex flex-wrap gap-1">
                    <span
                      v-for="r in e.reasons ?? []"
                      :key="r"
                      class="text-xs px-2 py-0.5 rounded-full bg-input text-fg-muted border border-border-c"
                    >{{ r }}</span>
                    <span v-if="!e.reasons?.length" class="text-fg-muted">—</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- the shared Entry Detail modal (own-entry view/edit) -->
      <EntryDetailModal
        :open="modalOpen"
        :entry-id="modalEntryId"
        @close="modalOpen = false"
        @saved="onSaved"
      />
    </template>
  </div>
</template>
